/**
 * Indication Manager Component
 *
 * Admin interface for managing indications (NCIt-based) with therapeutic area linking.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Indication, TherapeuticArea } from '../../types';

interface IndicationManagerProps {
  indications: Indication[];
  therapeuticAreas: TherapeuticArea[];
  onUpdate: (indications: Indication[]) => Promise<void>;
}

const IndicationManager: React.FC<IndicationManagerProps> = ({
  indications,
  therapeuticAreas,
  onUpdate,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Indication>>({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newIndication, setNewIndication] = useState<Partial<Indication>>({
    code: '',
    name: '',
    therapeuticArea: '',
    description: '',
  });
  const [filterTA, setFilterTA] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Filter indications by therapeutic area
  const filteredIndications = indications.filter((ind) => {
    if (filterTA === 'all') return true;
    return ind.therapeuticArea === filterTA;
  });

  // Get therapeutic area name
  const getTAName = (code?: string) => {
    if (!code) return 'Unassigned';
    const ta = therapeuticAreas.find((t) => t.code === code);
    return ta ? ta.name : code;
  };

  const handleEdit = (indication: Indication) => {
    setEditingId(indication.code);
    setEditForm(indication);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editForm.code || !editForm.name) {
      setError('Code and name are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedIndications = indications.map((ind) =>
        ind.code === editingId ? (editForm as Indication) : ind
      );
      await onUpdate(updatedIndications);
      setEditingId(null);
      setEditForm({});
    } catch (err: any) {
      setError(err.message || 'Failed to update indication');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm('Delete this indication?')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedIndications = indications.filter((ind) => ind.code !== code);
      await onUpdate(updatedIndications);
    } catch (err: any) {
      setError(err.message || 'Failed to delete indication');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newIndication.code?.trim() || !newIndication.name?.trim()) {
      setError('Code and name are required');
      return;
    }

    // Check for duplicate code
    if (indications.some((ind) => ind.code === newIndication.code)) {
      setError('Code already exists');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedIndications = [...indications, newIndication as Indication];
      await onUpdate(updatedIndications);
      setNewIndication({ code: '', name: '', therapeuticArea: '', description: '' });
      setAddDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add indication');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={saving}
          >
            Add Indication
          </Button>
          <TextField
            select
            size="small"
            label="Filter by Therapeutic Area"
            value={filterTA}
            onChange={(e) => setFilterTA(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All ({indications.length})</MenuItem>
            {therapeuticAreas.map((ta) => {
              const count = indications.filter((ind) => ind.therapeuticArea === ta.code).length;
              return (
                <MenuItem key={ta.code} value={ta.code}>
                  {ta.name} ({count})
                </MenuItem>
              );
            })}
          </TextField>
        </Box>
        <Alert severity="info" sx={{ py: 0.5 }}>
          Showing {filteredIndications.length} of {indications.length} indications
        </Alert>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="12%">Code</TableCell>
              <TableCell width="25%">Name</TableCell>
              <TableCell width="18%">Therapeutic Area</TableCell>
              <TableCell width="35%">Description</TableCell>
              <TableCell width="10%" align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIndications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {filterTA === 'all'
                    ? 'No indications yet'
                    : 'No indications for selected therapeutic area'}
                </TableCell>
              </TableRow>
            ) : (
              filteredIndications.map((indication) => (
                <TableRow key={indication.code}>
                  {editingId === indication.code ? (
                    <>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={editForm.code || ''}
                          onChange={(e) =>
                            setEditForm((prev) => ({ ...prev, code: e.target.value }))
                          }
                          disabled
                          helperText="Code cannot be changed"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={editForm.name || ''}
                          onChange={(e) =>
                            setEditForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          disabled={saving}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={editForm.therapeuticArea || ''}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              therapeuticArea: e.target.value,
                            }))
                          }
                          disabled={saving}
                        >
                          <MenuItem value="">None</MenuItem>
                          {therapeuticAreas.map((ta) => (
                            <MenuItem key={ta.code} value={ta.code}>
                              {ta.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          value={editForm.description || ''}
                          onChange={(e) =>
                            setEditForm((prev) => ({ ...prev, description: e.target.value }))
                          }
                          disabled={saving}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveEdit}
                            disabled={saving}
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={saving}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{indication.code}</TableCell>
                      <TableCell>
                        <strong>{indication.name}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTAName(indication.therapeuticArea)}
                          size="small"
                          color={indication.therapeuticArea ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{indication.description || '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(indication)}
                            disabled={saving || editingId !== null}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(indication.code)}
                            disabled={saving || editingId !== null}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !saving && setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Indication</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Code"
              placeholder="e.g., IND030"
              value={newIndication.code}
              onChange={(e) =>
                setNewIndication((prev) => ({ ...prev, code: e.target.value }))
              }
              required
              helperText="Unique identifier (e.g., IND001, IND002)"
              disabled={saving}
            />
            <TextField
              label="Name"
              placeholder="e.g., Hypertension"
              value={newIndication.name}
              onChange={(e) =>
                setNewIndication((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              disabled={saving}
            />
            <TextField
              select
              label="Therapeutic Area"
              value={newIndication.therapeuticArea || ''}
              onChange={(e) =>
                setNewIndication((prev) => ({ ...prev, therapeuticArea: e.target.value }))
              }
              helperText="Link to therapeutic area (optional)"
              disabled={saving}
            >
              <MenuItem value="">None</MenuItem>
              {therapeuticAreas.map((ta) => (
                <MenuItem key={ta.code} value={ta.code}>
                  {ta.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              placeholder="Optional description"
              value={newIndication.description}
              onChange={(e) =>
                setNewIndication((prev) => ({ ...prev, description: e.target.value }))
              }
              multiline
              rows={3}
              disabled={saving}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving}>
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IndicationManager;
